import { Injectable } from '@angular/core';
import * as Sentry from '@sentry/angular-ivy';
import { NgbSlideEvent } from '@ng-bootstrap/ng-bootstrap';

export interface BannerImage {
    desktop: string;
    mobile: string;
    link?: string;
}

export interface BannerPlacement {
    images: BannerImage[];
    activeIndex?: number;
}

@Injectable({
    providedIn: 'root',
})
export class BannerTrackingService {
    private loggedImpressions: Set<string> = new Set<string>();

    public onBannerSlide(
        event: NgbSlideEvent,
        bannerKey: string,
        banners: Record<string, { images: BannerImage[]; activeIndex?: number }>
    ): void {
        const parts = event.current.split('-');
        const idx = parseInt(parts[parts.length - 1], 10);

        if (banners[bannerKey]) {
            banners[bannerKey].activeIndex = idx;
        }

        const impressionKey = `${bannerKey}-${idx}`;
        if (!this.loggedImpressions.has(impressionKey)) {
            this.loggedImpressions.add(impressionKey);

            let bannerUrl = 'No URL provided';
            const bannerImage = banners[bannerKey]?.images?.[idx];
            if (bannerImage) {
                bannerUrl = bannerImage.link || bannerUrl;
            }

            this.trackBannerEvent('impression', bannerKey, bannerUrl);
        }
    }
    public handleBannerImpression(
        index: number,
        bannerKey: string,
        banners: Record<string, { images: BannerImage[]; activeIndex?: number }>
    ): void {
        if (!banners[bannerKey]) return;

        banners[bannerKey].activeIndex = index;

        const impressionKey = `${bannerKey}-${index}`;
        if (!this.loggedImpressions.has(impressionKey)) {
            this.loggedImpressions.add(impressionKey);

            let bannerUrl = 'No URL provided';
            const bannerImage = banners[bannerKey]?.images?.[index];
            if (bannerImage) {
                bannerUrl = bannerImage.link || bannerUrl;
            }

            this.trackBannerEvent('impression', bannerKey, bannerUrl);
        }
    }

    public handleBannerClick(
        bannerKey: string,
        idx: number,
        banners: Record<string, { images: BannerImage[] }>,
        redirectFn: (page: string, key: string) => void,
        contextPage: string
    ): void {
        const bannerPlacement = banners[bannerKey];
        let bannerUrl = 'No URL provided';
        if (bannerPlacement?.images?.length > idx) {
            bannerUrl = bannerPlacement.images[idx].link || bannerUrl;
        }

        this.trackBannerEvent('click', bannerKey, bannerUrl);
        redirectFn(contextPage, bannerKey);
    }

    private trackBannerEvent(
        eventType: 'click' | 'impression',
        bannerKey: string,
        bannerUrl?: string
    ): void {
        Sentry.captureEvent({
            message: `Banner ${eventType} event on ${
                bannerUrl || 'No URL provided'
            }`,
            level: 'info',
            extra: {
                bannerKey,
                bannerUrl: bannerUrl || 'No URL provided',
            },
            tags: {
                eventType: `banner.${eventType}`,
            },
        });
        //  Push to Google Tag Manager (GTM)
        if (typeof window !== 'undefined' && (window as any).dataLayer) {
            (window as any).dataLayer.push({
                event: `banner.${eventType}`,
                bannerKey,
                bannerUrl: bannerUrl || 'No URL provided',
            });
        }
    }

    // Optionally, add a reset if needed
    public resetImpressions(): void {
        this.loggedImpressions.clear();
    }
}
